import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import GlobalLogo from '@/components/feature/GlobalLogo';

export default function ResetPasswordPage() {
  const { user, resetPassword, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  // Detect recovery flow: user has a session and hash indicates recovery
  useEffect(() => {
    const hash = window.location.hash;
    if (user && (hash.includes('type=recovery') || hash.includes('type=recovery'))) {
      setIsRecoveryMode(true);
    } else if (user) {
      // User has session but hash might already be cleared by SDK — still recovery
      setIsRecoveryMode(true);
    }
  }, [user]);

  const handleRequestReset = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!email.trim()) {
      setError('Ingresá tu email.');
      return;
    }

    setLoading(true);
    const { error: authError } = await resetPassword(email.trim());
    setLoading(false);

    if (authError) {
      setError(authError.message || 'Error al enviar el email de recuperación.');
      return;
    }

    setSuccess('Te enviamos un link a tu email para que puedas elegir una nueva contraseña.');
    setEmail('');
  };

  const handleSetNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPassword.trim()) {
      setError('Ingresá la nueva contraseña.');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    const { error: authError } = await updatePassword(newPassword);
    setLoading(false);

    if (authError) {
      setError(authError.message || 'Error al actualizar la contraseña.');
      return;
    }

    setSuccess('Contraseña actualizada correctamente.');

    // Sign out and redirect to login after short delay
    setTimeout(async () => {
      await signOut();
      navigate('/login');
    }, 2000);
  };

  // ── Recovery Mode: Set New Password ──
  if (isRecoveryMode) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <GlobalLogo />
            </Link>
            <h1 className="text-2xl font-semibold text-foreground-950 mt-6">
              Nueva contraseña
            </h1>
            <p className="text-sm text-foreground-600 mt-2">
              Elegí una contraseña nueva para tu cuenta
            </p>
          </div>

          <form
            onSubmit={handleSetNewPassword}
            className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-5"
          >
            {error && (
              <div className="bg-accent-100 text-accent-900 text-sm px-4 py-3 rounded-md border border-accent-200">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-primary-100 text-primary-900 text-sm px-4 py-3 rounded-md border border-primary-200">
                {success}
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Nueva contraseña
              </label>
              <input
                id="newPassword"
                type="password"
                name="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="Mínimo 6 caracteres"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Confirmar nueva contraseña
              </label>
              <input
                id="confirmNewPassword"
                type="password"
                name="confirmNewPassword"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
                placeholder="Repetí la contraseña"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {loading ? 'Actualizando...' : 'Actualizar contraseña'}
            </button>
          </form>

          <p className="text-center text-sm text-foreground-600 mt-6">
            <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium transition">
              Volver al inicio de sesión
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Default Mode: Request Reset Link ──
  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <GlobalLogo />
          </Link>
          <h1 className="text-2xl font-semibold text-foreground-950 mt-6">
            Recuperar contraseña
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            Te enviamos un link para que elijas una nueva
          </p>
        </div>

        <form
          onSubmit={handleRequestReset}
          className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-5"
        >
          {error && (
            <div className="bg-accent-100 text-accent-900 text-sm px-4 py-3 rounded-md border border-accent-200">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-primary-100 text-primary-900 text-sm px-4 py-3 rounded-md border border-primary-200">
              {success}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-md bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition"
              placeholder="tu@email.com"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {loading ? 'Enviando...' : 'Enviar link de recuperación'}
          </button>
        </form>

        <p className="text-center text-sm text-foreground-600 mt-6">
          <Link to="/login" className="text-primary-500 hover:text-primary-600 font-medium transition">
            Volver al inicio de sesión
          </Link>
        </p>
      </div>
    </div>
  );
}